// admin/src/spinner.ts
// Full-page spinner backdrop — covers everything with bg-black/60 +
// backdrop-blur-sm (same style as the other modals — login/confirm/
// add-group) and shows a centered spinner ring + optional message.
//
// Reusable for any "waiting for server response" pause. Layered ABOVE
// every other modal (z-120) so it can wrap multi-step flows too.
//
// Markup lives in admin/index.php at the top of <body>:
//   <div id="spinner-backdrop" class="hidden fixed inset-0 z-[120]
//        flex items-center justify-center bg-black/60 backdrop-blur-sm">
//       <div class="flex flex-col items-center gap-3">
//           <svg id="spinner-ring" ...>  <!-- animated SVG ring -->
//           <p id="spinner-message" class="text-white/90 text-sm">Loading…</p>
//       </div>
//   </div>
//
// Public API:
//   showSpinner(message?)  — shows the backdrop, optionally sets text
//   hideSpinner()          — hides the backdrop
//   setSpinnerMessage(msg) — updates text without re-opening
//   isSpinnerVisible()     — boolean for callers that want to be idempotent
//
// Idempotency: showSpinner() does NOT add a duplicate backdrop if one is
// already shown — but it DOES update the message. hideSpinner() is safe
// to call when hidden (no-op).

const BACKDROP_ID = 'spinner-backdrop';
const RING_ID     = 'spinner-ring';
const MESSAGE_ID  = 'spinner-message';

function backdrop(): HTMLElement | null {
  return document.getElementById(BACKDROP_ID);
}

function ring(): HTMLElement | null {
  return document.getElementById(RING_ID);
}

function msgEl(): HTMLElement | null {
  return document.getElementById(MESSAGE_ID);
}

export function showSpinner(message?: string): void {
  const el = backdrop();
  if (!el) {
    // Spinner is a UX nicety; don't crash callers if the host page
    // hasn't loaded it (e.g. tests, or a future page that forgets it).
    console.warn('[timestop] showSpinner: #spinner-backdrop not in DOM');
    return;
  }
  if (message !== undefined) {
    const m = msgEl();
    if (m) m.textContent = message;
  }
  if (el.classList.contains('hidden')) {
    el.classList.remove('hidden');
    el.classList.add('flex');
  }
  // Reset the ring animation in case it was paused (e.g. via
  // prefers-reduced-motion detection in the future).
  const r = ring();
  if (r && r.classList.contains('paused')) r.classList.remove('paused');
}

export function hideSpinner(): void {
  const el = backdrop();
  if (!el) return;
  if (!el.classList.contains('hidden')) {
    el.classList.add('hidden');
    el.classList.remove('flex');
  }
}

export function setSpinnerMessage(message: string): void {
  const m = msgEl();
  if (m) m.textContent = message;
}

export function isSpinnerVisible(): boolean {
  const el = backdrop();
  return !!el && !el.classList.contains('hidden');
}

